/* Copyright (c) 2020-present CloudIO Inc */

import { BaseEditor } from 'slate';
import { HistoryEditor } from 'slate-history';
import { ReactEditor } from 'slate-react';
import isHotkey from 'is-hotkey';

export type CustomElement = {
  type: BlockType;
  url?: string;
  children: CustomText[];
  option?: Option;
};

export type EmptyText = {
  text: string;
};

export type CustomText = {
  text: string;
  bold?: true;
  italic?: true;
  underline?: true;
  code?: true;
  strikethrough?: true;
  color?: string;
  backgroundColor?: string;
};

export type Option = {
  value: string;
  label?: string;
};

export interface MentionElement {
  type: 'mention';
  option: Option;
  children: EmptyText[];
}

export interface IOEditor extends BaseEditor {
  skipListNormalizing?: boolean;
}

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor & IOEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

export const MARK_HOTKEYS: [(e: React.KeyboardEvent) => boolean, MarkType][] = [
  [isHotkey('mod+b'), 'bold'],
  [isHotkey('mod+i'), 'italic'],
  [isHotkey('mod+u'), 'underline'],
  [isHotkey('mod+`'), 'code'],
];

export const BLOCK_HOTKEYS: [(e: React.KeyboardEvent) => boolean, BlockType][] =
  [
    [isHotkey('mod+shift+1'), 'h1'],
    [isHotkey('mod+shift+2'), 'h2'],
    [isHotkey('mod+shift+3'), 'h3'],
    [isHotkey('mod+shift+p'), 'p'],
    [isHotkey('mod+]'), 'quote'],
  ];

export type MarkType =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'code'
  | 'strikethrough';

export type BlockType =
  | 'a'
  | 'code'
  | 'error'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'img'
  | 'info'
  | 'li'
  | 'mention'
  | 'ol'
  | 'p'
  | 'quote'
  | 'success'
  | 'ul'
  | 'video'
  | 'warning';

export const LIST_TYPES: BlockType[] = ['ol', 'ul'];

export const ELEMENT_TAGS = {
  A: (el: HTMLElement) => ({
    type: 'a',
    url: el.getAttribute('href'),
  }),
  BLOCKQUOTE: () => ({ type: 'quote' }),
  H1: () => ({ type: 'h1' }),
  H2: () => ({ type: 'h2' }),
  H3: () => ({ type: 'h3' }),
  H4: () => ({ type: 'h4' }),
  H5: () => ({ type: 'h5' }),
  H6: () => ({ type: 'h6' }),
  IMG: (el: HTMLElement) => ({
    type: 'img',
    url: el.getAttribute('src'),
  }),
  LI: () => ({ type: 'li' }),
  OL: () => ({ type: 'ol' }),
  P: () => ({ type: 'p' }),
  PRE: () => ({ type: 'code' }),
  UL: () => ({ type: 'ul' }),
};

export type ElementTags = keyof typeof ELEMENT_TAGS;

// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
export const TEXT_TAGS = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
};

export type TextTags = keyof typeof TEXT_TAGS;

export type El = CustomElement | CustomText | string | null;
